import { t } from '@lingui/core/macro';
import { PaymentOption, QuoteStatus, TireQuoteRecordCustomer } from '@navio/nsdk';
import { noddiAsync, URLKeys } from 'noddi-async';
import { NoddiButton, NoddiStepper } from 'noddi-ui-web';
import { customerRoutes, NoddiDateFormats, noddiFormatDate } from 'noddi-util';
import { log } from 'noddi-web';
import { useNavigate } from 'react-router-dom';
import { routePaymentSession } from '../../utils/paymentSession';
import { shouldPayForOrder } from '../../utils/tireOfferPayment';

interface Step {
  label: string;
  description: string;
  key: (QuoteStatus | 'ORDER_PROCESSING')[];
  content?: React.ReactNode;
}

function getStepsWithTranslation({
  estimatedDeliveryDate,
  orderAcceptedButShouldPay,
  hasActiveTireHotelContractForWheelSet,
  isInvoicedOrder
}: {
  estimatedDeliveryDate?: string | null;
  orderAcceptedButShouldPay: boolean;
  hasActiveTireHotelContractForWheelSet: boolean;
  isInvoicedOrder: boolean;
}): Step[] {
  const estimatedDeliveryDateFormatted = noddiFormatDate(estimatedDeliveryDate, NoddiDateFormats.DATE);
  const translatedDescription = estimatedDeliveryDateFormatted
    ? `. ${t`They are estimated to arrive at our warehouse on`} ${estimatedDeliveryDateFormatted}`
    : '';

  const tiresArrivedExtraInfo = hasActiveTireHotelContractForWheelSet
    ? t`We will mount them on your rims at our tire hotel.`
    : t`We will contact you to book an appointment to have them mounted on your car.`;

  const mountedDescription = hasActiveTireHotelContractForWheelSet
    ? t`The tires have been mounted on your rims at our tire hotel.`
    : t`The tires have been mounted on your car.`;

  const stepsForMountingAtCustomerFlow = [
    {
      label: t`Tire mounting proposal sent`,
      description: t`We have sent a booking proposal for having your tires mounted on your car. Check the SMS for details.`,
      key: [QuoteStatus.BOOKING_PROPOSAL_TIRE_MOUNT_SENT_TO_CUSTOMER]
    },
    {
      label: t`Tire mounting booked`,
      description: t`An appointment for having your tires mounted on your car has been scheduled. Check the SMS for details.`,
      key: [QuoteStatus.TIRE_MOUNTING_BOOKED]
    }
  ];
  return [
    {
      label: t`Order confirmed`,
      description: isInvoicedOrder
        ? t`Your order has been confirmed.`
        : orderAcceptedButShouldPay
          ? t`Your order has been confirmed. Please pay for your order in order for us to process it.`
          : t`Your order has been confirmed and paid.`,
      key: [QuoteStatus.ACCEPTED]
    },
    {
      label: t`Order processing`,
      description: t`Our team is processing your order. You will receive an SMS with estimated delivery time when the tires are ordered from our main warehouse.`,
      key: ['ORDER_PROCESSING']
    },
    {
      label: t`Your tires are on the way`,
      description: t`Your tires are on the way` + translatedDescription,
      key: [QuoteStatus.INVENTORY_ORDERED_AT_SUPPLIERS_IN_FULL, QuoteStatus.INVENTORY_ORDERED_AT_SUPPLIERS_PARTIAL]
    },
    {
      label: t`Tires arrived`,
      description: `${t`Your tires have arrived.`} ${tiresArrivedExtraInfo}`,
      key: [
        QuoteStatus.INVENTORY_RECEIVED_IN_FULL,
        QuoteStatus.INVENTORY_RECEIVED_PARTIAL,
        QuoteStatus.TIRES_RECEIVED_AWAITING_MOUNTING_ON_RIM_AT_STORAGE_FACILITY
      ]
    },
    ...(hasActiveTireHotelContractForWheelSet ? [] : stepsForMountingAtCustomerFlow),
    {
      label: t`Tires mounted`,
      description: mountedDescription,
      key: [
        QuoteStatus.TIRES_MOUNTED_ON_WHEELS_AT_STORAGE_FACILITY,
        QuoteStatus.TIRES_MOUNTED_ON_WHEELS_AT_CUSTOMER,
        QuoteStatus.FULFILLED
      ]
    }
  ];
}

type StatusUpdatesProps = {
  tireOffer: TireQuoteRecordCustomer;
};

export const StatusUpdates = ({ tireOffer }: StatusUpdatesProps) => {
  const navigate = useNavigate();
  const selectedQuoteItem = tireOffer.quoteItems.find((quoteItem) => quoteItem.isSelected);

  const orderAcceptedButShouldPay = shouldPayForOrder({
    paymentMethodInTireOffer: tireOffer.paymentMethod,
    isPaid: tireOffer.isPaid,
    isSelected: selectedQuoteItem?.isSelected ?? false
  });

  const statusValue =
    'status' in tireOffer && typeof tireOffer.status === 'object' && tireOffer.status !== null
      ? (tireOffer.status as { status?: string | QuoteStatus }).status
      : tireOffer.status?.status;

  const isProcessing =
    (statusValue === QuoteStatus.ACCEPTED && tireOffer.paymentMethod === PaymentOption.INVOICE) ||
    (statusValue === QuoteStatus.ACCEPTED && tireOffer.isPaid);

  const status = isProcessing ? 'ORDER_PROCESSING' : statusValue;

  const steps = getStepsWithTranslation({
    estimatedDeliveryDate: selectedQuoteItem?.quoteItemTires[0]?.inventoryOrder?.estimatedDeliveryDate,
    orderAcceptedButShouldPay,
    hasActiveTireHotelContractForWheelSet: tireOffer.hasActiveTireHotelContractForWheelSet,
    isInvoicedOrder: tireOffer.paymentMethod === PaymentOption.INVOICE
  });
  const activeStep = steps.findIndex((step) => status && step.key.includes(status as QuoteStatus | 'ORDER_PROCESSING'));

  const { mutateAsync: createPaymentSession, isPending: isPaymentSessionPending } = noddiAsync.usePost({
    type: URLKeys.postOrderPaymentSession,
    queryConfig: {
      onSuccess: ({ data }) => {
        routePaymentSession({
          session: data,
          // oxlint-disable-next-line typescript/no-misused-promises -- routePaymentSession takes navigate typed () => void; react-router's navigate returns a promise that only tracks lazy-route loading
          navigate,
          getEmbeddedPath: (session) => customerRoutes.tireOfferConfirmation.getPath({ sessionId: session.sessionId })
        });
      }
    }
  });

  const handleCreatePaymentSession = async () => {
    if (tireOffer.order?.id) {
      await createPaymentSession({ orderId: tireOffer.order.id });
    } else {
      log({
        value: `No order ID found for tire offer. TireOfferId: ${tireOffer.id}, PaymentMethod: ${tireOffer.paymentMethod}, IsPaid: ${tireOffer.isPaid}, Status: ${JSON.stringify(tireOffer.status)}, QuoteItemsCount: ${tireOffer.quoteItems?.length}, SelectedQuoteItemId: ${selectedQuoteItem?.id}, Order: ${JSON.stringify(tireOffer.order)}`,
        severity: 'error',
        cause: new Error('No order ID found for tire offer')
      });
    }
  };

  const stepsWithContent = steps.map((step) => {
    const shouldShowPayNowButton = orderAcceptedButShouldPay && step.key.includes(QuoteStatus.ACCEPTED);
    return {
      ...step,
      content: shouldShowPayNowButton ? (
        <NoddiButton
          loading={isPaymentSessionPending}
          className='mt-4'
          // oxlint-disable-next-line typescript/no-misused-promises -- async onClick runs handleCreatePaymentSession(); React discards the returned promise, so a rejection is unhandled unless the body catches
          onClick={handleCreatePaymentSession}
          variant='success'
        >
          {t`Pay now`}
        </NoddiButton>
      ) : undefined
    };
  });

  return <NoddiStepper steps={stepsWithContent} activeStep={activeStep} orientation='vertical' />;
};
